import { useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../layout/Header';
import LeadersSection from '../components/LeadersSection';
import FormationGestionDocumentaireCard from '../components/FormationGestionDocumentaireCard';
import ArbrePRIsMECard from '../components/ArbrePRIsMECard';
import LexChatbotPRIsMECard from '../components/LexChatbotPRIsMECard';
import AikupCard from '../components/AikupCard';
import ExpertVisionModal from '../components/ExpertVisionModal';
import GouvernanceCEModal from '../components/GouvernanceCEModal';
import ComiteTIPrismeModal from '../components/ComiteTIPrismeModal';
import PlanDirecteurTIModal from '../components/PlanDirecteurTIModal';
import PrismeModal from '../components/PrismeModal';
import GestionDocumentaireModal from '../components/GestionDocumentaireModal';
import RencontreSACyberModal from '../components/RencontreSACyberModal';
import VacancesDisponibilitesSAModal from '../components/VacancesDisponibilitesSAModal';
import SuiviClientsIAModal from '../components/SuiviClientsIAModal';
import AIInsightBubble from '../components/AIInsightBubble';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import type { SolutionsAffairesItem } from '../data/solutionsAffairesData';

export default function SolutionsAffaires() {
  const navigate = useNavigate();
  const [isGouvernanceCEOpen, setIsGouvernanceCEOpen] = useState(false);
  const [isGouvernanceSAOpen, setIsGouvernanceSAOpen] = useState(false);
  const [isRencontreSAOpen, setIsRencontreSAOpen] = useState(false);
  const [isPlanDirecteurTIOpen, setIsPlanDirecteurTIOpen] = useState(false);
  const [isPrismeOpen, setIsPrismeOpen] = useState(false);
  const [isGestionDocumentaireOpen, setIsGestionDocumentaireOpen] = useState(false);
  const [isRencontreSACyberOpen, setIsRencontreSACyberOpen] = useState(false);
  const [isVacancesSAOpen, setIsVacancesSAOpen] = useState(false);
  const [isSuiviClientsIAOpen, setIsSuiviClientsIAOpen] = useState(false);
  const [expertVisionModal, setExpertVisionModal] = useState<{
    isOpen: boolean;
    expert: 'dany' | 'yannick' | 'francois' | null;
    item: SolutionsAffairesItem | null;
  }>({
    isOpen: false,
    expert: null,
    item: null,
  });

  const solutionSections = [
    'Stratégie & Gouvernance TI',
    'Cybersécurité & Gestion des Risques',
    'Transformation Numérique & Innovation',
    'Données & Intelligence d’affaires',
    'Plateformes & Environnement de Travail',
    'Infrastructure & Opérations TI',
    'Expérience Client & Applications Métier',
  ];

  const leaders = [
    {
      name: 'Stéphane Paradis',
      title: "Directeur intérimaire Solutions d'affaires & Cybersécurité",
      avatar: '/SP.png',
      vision:
        "Stéphane apporte une approche structurée, collaborative et orientée résultats pour accompagner l'équipe et les clients vers des solutions d'affaires sécuritaires et durables.",
      attentes:
        "Stéphane s'attend à ce que PRIsME clarifie les priorités, les responsabilités et les suivis afin de livrer une expérience client cohérente, professionnelle et mesurable.",
      pagePRIsME:
        "La page PRIsME de Solutions d’affaires regroupe la gouvernance, la cybersécurité, les outils et les suivis nécessaires pour transformer les besoins en résultats concrets.",
    },
    {
      name: 'Mikael Drouin-Bouchard',
      title: 'Spécialiste en cybersécurité',
      avatar: '/MDB.png',
      vision:
        "Mikael met l’accent sur la rigueur et la cohérence: transformer les besoins en solutions, avec des contrôles sécurité pragmatiques et une exécution stable.",
      attentes:
        "Mikael s'attend à ce que PRIsME standardise nos façons de faire (cadres, modèles, validations) et améliore la qualité des livrables SA/Cyber. Son objectif : rendre la démarche plus simple à répéter, plus fiable et plus mesurable.",
      pagePRIsME:
        "La page PRIsME de Solutions d’affaires est notre point de convergence pour la méthode: gouvernance, référentiels, outils et documentation — pour livrer vite, bien et de façon sécuritaire.",
    },
    {
      name: 'Philippe Bolduc',
      title: 'Spécialiste en cybersécurité',
      avatar: '/PB.png',
      vision:
        "Philippe apporte une vision terrain, structurée et orientée résultats pour aligner les besoins d’affaires avec des solutions concrètes, tout en maintenant une posture de sécurité solide.",
      attentes:
        "Philippe s'attend à ce que PRIsME accélère l'analyse, la priorisation et l'exécution des demandes SA, avec des livrables clairs, traçables et sécuritaires. Son objectif : réduire le flou, augmenter la qualité et livrer plus vite.",
      pagePRIsME:
        "La page PRIsME de Solutions d’affaires consolide la gouvernance, les cadres et les outils internes pour cadrer les besoins, documenter les décisions et livrer des solutions alignées et sécurisées.",
    },
  ];

  const gouvernanceCECard: SolutionsAffairesItem = {
    id: 10001,
    title: 'Gouvernance CE',
    category: '',
    description: 'Cadre de gouvernance exécutive : axes, maturité, rôles, roadmap et rituels.',
    aiSuggestion: 'Afficher la structure de gouvernance CE pour aligner les priorités, la maturité et les responsabilités.',
    kpiScore: 96,
    imageUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&auto=format&fit=crop&q=80',
  };

  const gouvernanceSACard: SolutionsAffairesItem = {
    id: 10003,
    title: 'Gouvernance SA',
    category: '',
    description: 'Cadre de gouvernance de Solutions d’affaires : axes, maturité, rôles, roadmap et rituels.',
    aiSuggestion: 'Afficher la structure de gouvernance de Solutions d’affaires pour aligner les priorités, la maturité et les responsabilités.',
    kpiScore: 96,
    imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&auto=format&fit=crop&q=80',
  };

  const gouvernanceCELeaders = [
    { name: 'Dany', avatar: '/DB.png', title: 'Dany - Amélioration continue & innovation' },
    { name: 'Dany', avatar: '/DB.png', title: 'Dany - Directeur RH' },
    { name: 'François', avatar: '/FP.png', title: 'François Delisle - VP Sécurité & Conformité' },
  ];

  const gouvernanceSALeaders = [
    { name: 'Stéphane', avatar: '/SP.png', title: "Directeur intérimaire Solutions d'affaires & Cybersécurité" },
  ];

  const cybersecuriteTileLeaders = [
    { name: 'Stéphane', avatar: '/SP.png', title: "Directeur intérimaire Solutions d'affaires & Cybersécurité" },
    { name: 'Mikael', avatar: '/MDB.png', title: 'Spécialiste en cybersécurité' },
    { name: 'Philippe', avatar: '/PB.png', title: 'Spécialiste en cybersécurité' },
  ];

  const rencontreSACard: SolutionsAffairesItem = {
    id: 10002,
    title: 'Rencontre SA',
    category: '',
    description: 'Gestion complète des rencontres Solutions d’affaires : participants, points, notes, actions, résumé et validations.',
    aiSuggestion: 'Outil de gouvernance pour les rencontres SA dans PRIsME.',
    kpiScore: 95,
    imageUrl: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200&auto=format&fit=crop&q=80',
  };

  const cybersecuriteSubmenuCard: SolutionsAffairesItem = {
    id: 10004,
    title: 'Gestion des opérations',
    category: '',
    description: 'Documents, gestion des risques, suivis CyberOps, services actifs et outils opérationnels cybersécurité.',
    aiSuggestion: 'Ouvrir la sous-section opérationnelle Cybersécurité de Solutions d’affaires.',
    kpiScore: 94,
    imageUrl: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&auto=format&fit=crop&q=80',
  };

  const offresCybersecuriteCard: SolutionsAffairesItem = {
    id: 10006,
    title: 'Offres de cybersécurité',
    category: '',
    description: 'Gestion des prospects, pricing, propositions client, rabais et moteur de recommandations.',
    aiSuggestion: 'Ouvrir la section vente et pricing Edith pour les offres de cybersécurité.',
    kpiScore: 93,
    imageUrl: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&auto=format&fit=crop&q=80',
  };

  const subventionsFinancementCard: SolutionsAffairesItem = {
    id: 10005,
    title: 'Subventions & Financement',
    category: '',
    description: 'Sous-menu dédié aux programmes d’aide, leviers de financement et opportunités d’innovation.',
    aiSuggestion: 'Ouvrir la sous-section Subventions & Financement.',
    kpiScore: 92,
    imageUrl: 'https://images.unsplash.com/photo-1554224155-66989c705ab7?w=800&q=80',
  };

  const vacancesDisponibilitesSACard: SolutionsAffairesItem = {
    id: 14005,
    title: 'Vacances & disponibilités SA',
    category: '',
    description: 'Calendrier interne des vacances, absences planifiées et disponibilités de l’équipe Solutions d’affaires.',
    aiSuggestion: 'Consulter les disponibilités SA pour mieux coordonner les rencontres, projets et suivis clients.',
    kpiScore: 95,
    imageUrl: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&auto=format&fit=crop&q=80',
  };

  const suiviClientsIACard: SolutionsAffairesItem = {
    id: 16005,
    title: 'Suivi clients IA',
    category: '',
    description: 'Grille partagée pour suivre les approches, rencontres, besoins, démos et prochaines actions IA auprès des clients BZ.',
    aiSuggestion: 'Ouvrir le fichier de suivi clients IA pour mettre à jour les statuts, rencontres, commentaires et opportunités.',
    kpiScore: 96,
    imageUrl: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&auto=format&fit=crop&q=80',
  };

  const createPlaceholderCard = (id: number, title: string, description: string, imageUrl: string): SolutionsAffairesItem => ({
    id,
    title,
    category: '',
    description,
    aiSuggestion: `Card ${title} prête à être détaillée dans PRIsME.`,
    kpiScore: 90,
    imageUrl,
  });

  const sectionCards: Record<string, SolutionsAffairesItem[]> = {
    'Stratégie & Gouvernance TI': [
      createPlaceholderCard(11001, 'Plan directeur TI', 'Structurer la vision TI, les priorités, les investissements et la feuille de route.', 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&auto=format&fit=crop&q=80'),
      createPlaceholderCard(11002, 'Comité TI', 'Cadencer les décisions, suivis, arbitrages et actions de gouvernance TI.', 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200&auto=format&fit=crop&q=80'),
      createPlaceholderCard(11003, 'Alignement business / TI', 'Aligner les besoins d’affaires avec les priorités, capacités et livrables TI.', 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&auto=format&fit=crop&q=80'),
      createPlaceholderCard(11004, 'KPI & tableaux de bord', 'Suivre la performance, la maturité et les indicateurs clés de pilotage.', 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&auto=format&fit=crop&q=80'),
      createPlaceholderCard(11005, 'Rencontre SA/Cyber', 'Préparer une nouvelle card conjointe SA/Cyber à détailler ensemble ensuite.', 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=1200&auto=format&fit=crop&q=80'),
    ],
    'Transformation Numérique & Innovation': [
      createPlaceholderCard(12001, 'Automatisation', 'Identifier et déployer des automatisations à forte valeur pour les opérations.', 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80'),
      createPlaceholderCard(12002, 'IA / agents', 'Sous-menu pour présenter, intégrer et gouverner les bots, assistants et agents IA chez les clients.', 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&auto=format&fit=crop&q=80'),
      createPlaceholderCard(12003, 'Optimisation des processus', 'Cartographier, simplifier et améliorer les processus d’affaires critiques.', 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80'),
      createPlaceholderCard(12004, 'POC PRISME', 'Tester rapidement un concept PRIsME avant déploiement à plus grande échelle.', 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1200&auto=format&fit=crop&q=80'),
    ],
    'Données & Intelligence d’affaires': [
      createPlaceholderCard(13001, 'Structuration des données', 'Organiser les données pour supporter la gouvernance, l’analyse et les intégrations.', 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop&q=80'),
      createPlaceholderCard(13002, 'Power BI / Dashboards', 'Construire des tableaux de bord pour piloter les décisions et la performance.', 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&auto=format&fit=crop&q=80'),
      createPlaceholderCard(13003, 'Qualité des données', 'Améliorer la fiabilité, la cohérence et l’usage des données critiques.', 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1200&q=80'),
      createPlaceholderCard(13004, 'Prise de décision', 'Outiller la direction avec des données exploitables et des indicateurs décisionnels.', 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&auto=format&fit=crop'),
    ],
    'Plateformes & Environnement de Travail': [
      createPlaceholderCard(14001, 'PRIsME', 'Plateforme structurante pour gouverner, documenter, piloter et faire évoluer l’ensemble des opérations, décisions et initiatives de l’entreprise.', 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1200&auto=format&fit=crop&q=80'),
      createPlaceholderCard(14002, 'Gestion documentaire', 'Organiser, classer et gouverner la documentation opérationnelle et stratégique.', 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&auto=format&fit=crop'),
      createPlaceholderCard(14003, 'Collaboration', 'Améliorer la communication, la coordination et le partage entre équipes.', 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&auto=format&fit=crop'),
      vacancesDisponibilitesSACard,
      createPlaceholderCard(14004, 'Expérience employé', 'Faire évoluer l’environnement numérique pour soutenir l’efficacité et l’adoption.', 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&auto=format&fit=crop'),
    ],
    'Infrastructure & Opérations TI': [
      createPlaceholderCard(15001, 'Réseaux / cloud / serveurs', 'Structurer les fondations techniques pour supporter la stabilité et l’évolution.', 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&auto=format&fit=crop'),
      createPlaceholderCard(15002, 'Support TI', 'Organiser le support, les escalades et la qualité de service au quotidien.', 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&auto=format&fit=crop&q=80'),
      createPlaceholderCard(15003, 'Gestion des actifs', 'Suivre les équipements, licences, cycles de vie et dépendances TI.', 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1200&auto=format&fit=crop&q=80'),
      createPlaceholderCard(15004, 'Performance & disponibilité', 'Mesurer la stabilité, la capacité et la disponibilité des services TI.', 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop&q=80'),
    ],
    'Expérience Client & Applications Métier': [
      createPlaceholderCard(16001, 'CRM / ERP', 'Aligner les outils centraux de gestion client et d’opérations métier.', 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1200&q=80'),
      createPlaceholderCard(16002, 'Parcours client', 'Optimiser les étapes, interactions et points de friction du parcours client.', 'https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d?w=1200&auto=format&fit=crop&q=80'),
      suiviClientsIACard,
      createPlaceholderCard(16003, 'Intégrations', 'Connecter les systèmes et fluidifier les échanges de données métier.', 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&auto=format&fit=crop&q=80'),
      createPlaceholderCard(16004, 'Applications spécifiques', 'Cadrer les applications sur mesure selon les besoins et cas d’usage réels.', 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&auto=format&fit=crop&q=80'),
    ],
  };

  const strategieGovernanceCards = [
    gouvernanceCECard,
    gouvernanceSACard,
    rencontreSACard,
    ...(sectionCards['Stratégie & Gouvernance TI'] || []),
  ];

  return (
    <div className="flex-1 w-full max-w-none min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header
        title="Solutions d'affaires"
        subtitle="Excellence en solutions d'affaires BZ Inc. - Alignement stratégique, optimisation des processus et performance accrue."
      />

      <div className="w-full max-w-none px-6 py-6 xl:px-8 2xl:px-10">
        <div className="w-full max-w-none mb-12">
          <LeadersSection leaders={leaders} title="Nos Leaders - Solutions d'affaires & Cybersécurité" />
        </div>

        <div className="w-full max-w-none space-y-8 mb-12">
          {solutionSections.map((section, index) => (
            <motion.div
              key={section}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="w-full max-w-none bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden"
            >
              <div className="px-6 py-5 bg-gradient-to-r from-blue-900 via-sky-800 to-cyan-700 text-white">
                <h3 className="text-xl md:text-2xl font-bold">{section}</h3>
              </div>
              <div className="px-6 py-6 bg-gray-50/80 space-y-6">
                {section === 'Stratégie & Gouvernance TI' && (
                  <div className="w-full max-w-none grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                    {strategieGovernanceCards.map((card, strategyIndex) => (
                      <motion.div
                        key={card.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 + 0.05 + strategyIndex * 0.03 }}
                        className="w-full max-w-none"
                      >
                        <AikupCard
                          item={card}
                          onClick={() => {
                            if (card.id === gouvernanceCECard.id) setIsGouvernanceCEOpen(true);
                            else if (card.id === gouvernanceSACard.id) setIsGouvernanceSAOpen(true);
                            else if (card.id === rencontreSACard.id) setIsRencontreSAOpen(true);
                            else if (card.title === 'Rencontre SA/Cyber') setIsRencontreSACyberOpen(true);
                            else if (card.title === 'Plan directeur TI') setIsPlanDirecteurTIOpen(true);
                          }}
                          onExpertClick={(expert: 'dany' | 'yannick' | 'francois') => {
                            setExpertVisionModal({
                              isOpen: true,
                              expert,
                              item: card,
                            });
                          }}
                          customLeaders={card.id === gouvernanceCECard.id ? gouvernanceCELeaders : gouvernanceSALeaders}
                          stampText={card.id >= 11001 && card.title !== 'Plan directeur TI' && card.title !== 'Rencontre SA/Cyber' ? 'DEV' : undefined}
                          index={strategyIndex}
                        />
                      </motion.div>
                    ))}
                  </div>
                )}

                {section === 'Cybersécurité & Gestion des Risques' && (
                  <div className="w-full max-w-none grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                    {[cybersecuriteSubmenuCard, offresCybersecuriteCard].map((card, cyberIndex) => (
                      <motion.div
                        key={card.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 + 0.05 + cyberIndex * 0.03 }}
                        className="w-full max-w-none"
                      >
                        <AikupCard
                          item={card}
                          onClick={() => {
                            if (card.id === cybersecuriteSubmenuCard.id) {
                              navigate('/solutions-affaires/cybersecurite');
                            } else {
                              navigate('/solutions-affaires/offres-de-cybersecurite');
                            }
                          }}
                          onExpertClick={(expert: 'dany' | 'yannick' | 'francois') => {
                            setExpertVisionModal({
                              isOpen: true,
                              expert,
                              item: card,
                            });
                          }}
                          customLeaders={cybersecuriteTileLeaders}
                          index={cyberIndex}
                        />
                      </motion.div>
                    ))}
                  </div>
                )}

                {section === 'Transformation Numérique & Innovation' && (
                  <div className="w-full max-w-none grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 + 0.05 }}
                      className="w-full max-w-none"
                    >
                      <AikupCard
                        item={subventionsFinancementCard}
                        onClick={() => navigate('/solutions-affaires/subventions-financement')}
                        onExpertClick={(expert: 'dany' | 'yannick' | 'francois') => {
                          setExpertVisionModal({
                            isOpen: true,
                            expert,
                            item: subventionsFinancementCard,
                          });
                        }}
                        customLeaders={gouvernanceSALeaders}
                        index={0}
                      />
                    </motion.div>
                  </div>
                )}

                {section !== 'Stratégie & Gouvernance TI' && sectionCards[section] && sectionCards[section].length > 0 && (
                  <div className="w-full max-w-none grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                    {sectionCards[section].map((card, cardIndex) => (
                      <motion.div
                        key={card.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 + 0.08 + cardIndex * 0.03 }}
                        className="w-full max-w-none"
                      >
                        <AikupCard
                          item={card}
                          onClick={() => {
                            if (card.title === 'Plan directeur TI') {
                              setIsPlanDirecteurTIOpen(true);
                            } else if (card.title === 'PRIsME') {
                              setIsPrismeOpen(true);
                            } else if (card.title === 'Gestion documentaire') {
                              setIsGestionDocumentaireOpen(true);
                            } else if (card.title === 'IA / agents') {
                              navigate('/ia-agentic');
                            } else if (card.title === 'Vacances & disponibilités SA') {
                              setIsVacancesSAOpen(true);
                            } else if (card.title === 'Suivi clients IA') {
                              setIsSuiviClientsIAOpen(true);
                            }
                          }}
                          onExpertClick={(expert: 'dany' | 'yannick' | 'francois') => {
                            setExpertVisionModal({
                              isOpen: true,
                              expert,
                              item: card,
                            });
                          }}
                          customLeaders={gouvernanceSALeaders}
                          stampText={card.title === 'Plan directeur TI' || card.title === 'PRIsME' || card.title === 'IA / agents' || card.title === 'Vacances & disponibilités SA' || card.title === 'Suivi clients IA' ? undefined : 'DEV'}
                          index={cardIndex}
                        />
                      </motion.div>
                    ))}
                  </div>
                )}

              </div>
            </motion.div>
          ))}
        </div>

        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-6 mb-12 md:grid-cols-2 xl:grid-cols-4">
          <FormationGestionDocumentaireCard
            department="Cybersécurité"
            domain="Cybersécurité"
            index={0}
          />
          <ArbrePRIsMECard
            index={1}
          />

          <LexChatbotPRIsMECard
            department="Solutions d’affaires"
            domain="Solutions d’affaires"
            index={2}
            background="bz-blue"
          />

          <button
            onClick={() => navigate('/gestion-documentaire')}
            className="relative overflow-hidden rounded-3xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-72 group border border-cyan-200 bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-800"
            type="button"
          >
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.35),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.18),transparent_30%)]" />

            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-white/5 group-hover:from-cyan-950/45 group-hover:via-sky-800/10 transition-all duration-300" />

            <div className="relative h-full p-6 flex flex-col justify-end text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center shadow-inner">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold drop-shadow">Gestion Documentaire PRIsME – Cyber</h3>
              </div>
              <p className="text-xs text-white/90 leading-relaxed drop-shadow mb-1">
                Plans cybersécurité, politiques, rapports NIST / Loi 25, tous générés avec PRIsME + IA puis validés par
                l&apos;équipe cybersécurité BZ.
              </p>
              <span className="inline-flex items-center gap-2 text-[11px] font-semibold text-cyan-100 mt-1">
                Explorer les plans & politiques
                <span className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-[10px] group-hover:translate-x-0.5 transition-transform">
                  →
                </span>
              </span>
            </div>
          </button>
        </div>
      </div>

      <GouvernanceCEModal
        isOpen={isGouvernanceCEOpen}
        onClose={() => setIsGouvernanceCEOpen(false)}
      />

      <GouvernanceCEModal
        isOpen={isGouvernanceSAOpen}
        onClose={() => setIsGouvernanceSAOpen(false)}
        variant="sa"
      />

      <ComiteTIPrismeModal
        isOpen={isRencontreSAOpen}
        onClose={() => setIsRencontreSAOpen(false)}
        title="🤝 Rencontre SA"
        scope="SA"
      />

      <PlanDirecteurTIModal
        isOpen={isPlanDirecteurTIOpen}
        onClose={() => setIsPlanDirecteurTIOpen(false)}
      />

      <PrismeModal
        isOpen={isPrismeOpen}
        onClose={() => setIsPrismeOpen(false)}
      />

      <GestionDocumentaireModal
        isOpen={isGestionDocumentaireOpen}
        onClose={() => setIsGestionDocumentaireOpen(false)}
      />

      <RencontreSACyberModal
        isOpen={isRencontreSACyberOpen}
        onClose={() => setIsRencontreSACyberOpen(false)}
      />

      <VacancesDisponibilitesSAModal
        isOpen={isVacancesSAOpen}
        onClose={() => setIsVacancesSAOpen(false)}
      />

      <SuiviClientsIAModal
        isOpen={isSuiviClientsIAOpen}
        onClose={() => setIsSuiviClientsIAOpen(false)}
      />

      <ExpertVisionModal
        isOpen={expertVisionModal.isOpen}
        onClose={() => setExpertVisionModal({ isOpen: false, expert: null, item: null })}
        expert={expertVisionModal.expert}
        item={expertVisionModal.item}
      />

      <AIInsightBubble />
    </div>
  );
}
